import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { invokeEdgeFunction } from '../../lib/supabaseEdge'
import { useAuth } from '../../lib/AuthContext'
import {
  pageWrap,
  pageTitle,
  pageSubtitle,
  pageHint,
  toolbarCard,
  tablePanel,
  tableHeaderBar,
  th,
  td,
  infoBanner,
  emptyState,
  btnPrimary,
  btnSecondary,
  actionsRow,
  formatSyncStatus,
  statusBadge,
} from './knowledgePageLayout'
import ConfirmDialog from '../ConfirmDialog'

const ACTIVE_STATUSES = new Set(['queued', 'parsing', 'embedding'])
const POLL_MS = 3000

function formatBytes(n) {
  const v = Number(n) || 0
  if (v < 1024) return `${v} B`
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`
  return `${(v / (1024 * 1024)).toFixed(1)} MB`
}

function StatusCell({ status, lastError }) {
  const colors = statusBadge(status)
  return (
    <div>
      <span
        style={{
          display: 'inline-block',
          fontSize: 12,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 999,
          background: colors.bg,
          color: colors.color,
        }}
      >
        {formatSyncStatus(status)}
      </span>
      {lastError && (status === 'parse_failed' || status === 'embed_failed') ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: '#b91c1c',
            maxWidth: 280,
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
          title={lastError}
        >
          {lastError}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Personal Library — private uploads for the signed-in user.
 * Page chrome renders immediately; only flag / quota / file list show loading.
 */
export default function PersonalKnowledge() {
  const { user, profile } = useAuth()
  const fileRef = useRef(null)
  const [flagOn, setFlagOn] = useState(false)
  const [flagReady, setFlagReady] = useState(false)
  const [docsLoading, setDocsLoading] = useState(false)
  const [quotaLoading, setQuotaLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [docs, setDocs] = useState([])
  const [usedBytes, setUsedBytes] = useState(0)
  const [maxBytes, setMaxBytes] = useState(524288000)
  const [agentOn, setAgentOn] = useState(Boolean(profile?.allow_agent_personal_kb))
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteError, setDeleteError] = useState('')

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!user?.id) return

      if (!silent) setError('')

      const { data: cfg } = await supabase
        .from('kb_pipeline_configs')
        .select('enable_personal_knowledge')
        .is('source_id', null)
        .maybeSingle()
      const enabled = cfg?.enable_personal_knowledge === true
      setFlagOn(enabled)
      setFlagReady(true)

      if (!enabled) {
        setDocs([])
        setDocsLoading(false)
        setQuotaLoading(false)
        return
      }

      if (!silent) {
        setDocsLoading(true)
        setQuotaLoading(true)
      }

      const docsPromise = supabase
        .from('kb_documents')
        .select(
          'id, title, sync_status, metadata, created_at, updated_at, kb_document_files(file_size_bytes, mime_type)'
        )
        .eq('library', 'personal')
        .eq('owner_user_id', user.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(200)

      const quotaPromise = invokeEdgeFunction('user-knowledge-upload', {
        method: 'POST',
        body: JSON.stringify({ action: 'quota' }),
      })

      const [docsRes, quotaRes] = await Promise.all([docsPromise, quotaPromise])

      if (docsRes.error) {
        if (!silent) setError(docsRes.error.message)
      } else {
        setDocs(docsRes.data ?? [])
      }
      setDocsLoading(false)

      if (quotaRes.ok) {
        const body = await quotaRes.json().catch(() => ({}))
        setUsedBytes(Number(body.used_bytes) || 0)
        setMaxBytes(Number(body.max_bytes) || 524288000)
      }
      setQuotaLoading(false)

      if (!silent) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('allow_agent_personal_kb')
          .eq('id', user.id)
          .maybeSingle()
        setAgentOn(Boolean(prof?.allow_agent_personal_kb))
      }
    },
    [user?.id]
  )

  useEffect(() => {
    if (user?.id) load()
  }, [user?.id, load])

  const hasActive = docs.some((d) => ACTIVE_STATUSES.has(d.sync_status))

  useEffect(() => {
    if (!flagOn || !hasActive) return undefined
    const id = window.setInterval(() => {
      void load({ silent: true })
    }, POLL_MS)
    return () => window.clearInterval(id)
  }, [flagOn, hasActive, load])

  const onUpload = async (file) => {
    if (!file || busy || !flagReady || !flagOn) return
    setBusy(true)
    setError('')
    setMsg('')
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await invokeEdgeFunction('user-knowledge-upload', {
        method: 'POST',
        body: form,
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.message ?? `Upload failed (${res.status})`)
      if (body.duplicate) {
        setMsg(`Already indexed: ${body.title || body.existing_document_id}`)
      } else {
        setMsg(`Queued for indexing: ${file.name}`)
      }
      await load({ silent: true })
    } catch (e) {
      setError(e?.message ?? 'Upload failed')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const requestDelete = (documentId, title) => {
    if (busy) return
    setDeleteError('')
    setDeleteTarget({ id: documentId, title: title || 'this file' })
  }

  const confirmDelete = async () => {
    if (!deleteTarget?.id || busy) return
    setBusy(true)
    setDeleteError('')
    setError('')
    setMsg('')
    try {
      const res = await invokeEdgeFunction('user-knowledge-upload', {
        method: 'POST',
        body: JSON.stringify({ action: 'delete', document_id: deleteTarget.id }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.message ?? 'Delete failed')
      setDeleteTarget(null)
      setMsg('Removed file and search index.')
      await load({ silent: true })
    } catch (e) {
      setDeleteError(e?.message ?? 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  const onToggleAgent = async () => {
    const next = !agentOn
    setAgentOn(next)
    const { error: rpcErr } = await supabase.rpc('set_allow_agent_personal_kb', {
      p_enabled: next,
    })
    if (rpcErr) {
      setAgentOn(!next)
      setError(rpcErr.message)
    }
  }

  const pct = maxBytes > 0 ? Math.min(100, Math.round((usedBytes / maxBytes) * 100)) : 0
  const uploadEnabled = flagReady && flagOn && !busy

  return (
    <div style={pageWrap}>
      <h1 style={pageTitle}>Personal Library</h1>
      <p style={pageSubtitle}>Private to you. Not shared with teammates.</p>
      <p style={pageHint}>
        To search these with company knowledge, open{' '}
        <Link to="/knowledge" style={{ color: '#2563eb' }}>
          Knowledge Search
        </Link>{' '}
        and check Personal. Status updates automatically while indexing (Queued → Parsing → Embedding → Indexed).
      </p>

      {!flagReady ? (
        <div style={{ ...infoBanner, marginBottom: 16 }}>Checking Personal Library availability…</div>
      ) : null}

      {flagReady && !flagOn ? (
        <div style={{ ...infoBanner, marginBottom: 16 }}>
          Personal Library is disabled. An admin can turn on{' '}
          <strong>Personal Library</strong> under Knowledge → Access → Settings.
        </div>
      ) : null}

      {flagReady && flagOn ? (
        <>
          <div style={toolbarCard}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>
                {quotaLoading
                  ? 'Storage …'
                  : `Storage ${formatBytes(usedBytes)} / ${formatBytes(maxBytes)} (${pct}%)`}
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 999,
                  background: '#e2e8f0',
                  overflow: 'hidden',
                  opacity: quotaLoading ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    width: quotaLoading ? '30%' : `${pct}%`,
                    height: '100%',
                    background: !quotaLoading && pct >= 90 ? '#dc2626' : '#2563eb',
                    transition: 'width 0.2s ease',
                  }}
                />
              </div>
            </div>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 14,
                color: '#334155',
                marginBottom: 16,
                cursor: 'pointer',
              }}
            >
              <input type="checkbox" checked={agentOn} onChange={onToggleAgent} />
              Allow agents to use my personal library (off by default)
            </label>

            <div
              style={{
                border: '1px dashed #cbd5e1',
                borderRadius: 10,
                padding: 20,
                textAlign: 'center',
                background: '#f8fafc',
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,.xlsx,.xls,.doc,.txt,.md"
                style={{ display: 'none' }}
                onChange={(e) => onUpload(e.target.files?.[0])}
              />
              <button
                type="button"
                style={btnPrimary}
                disabled={!uploadEnabled}
                onClick={() => fileRef.current?.click()}
              >
                {busy ? 'Working…' : 'Choose file'}
              </button>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                PDF, DOCX, XLSX, TXT · max 50 MB each
              </div>
            </div>

            {error ? (
              <div
                style={{
                  ...infoBanner,
                  marginTop: 12,
                  borderColor: '#fecaca',
                  background: '#fef2f2',
                  color: '#b91c1c',
                }}
              >
                {error}
              </div>
            ) : null}
            {msg ? (
              <div
                style={{
                  ...infoBanner,
                  marginTop: 12,
                  borderColor: '#bbf7d0',
                  background: '#f0fdf4',
                  color: '#166534',
                }}
              >
                {msg}
              </div>
            ) : null}
          </div>

          <div style={{ ...tablePanel, marginTop: 16 }}>
            <div style={tableHeaderBar}>
              My files
              {hasActive ? (
                <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, color: '#64748b' }}>
                  Indexing…
                </span>
              ) : null}
            </div>
            {docsLoading ? (
              <div style={emptyState}>Loading files…</div>
            ) : docs.length === 0 ? (
              <div style={emptyState}>No personal files yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Name</th>
                    <th style={th}>Status</th>
                    <th style={th}>Size</th>
                    <th style={th} />
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => {
                    const size = (d.kb_document_files ?? []).reduce(
                      (s, f) => s + Number(f.file_size_bytes || 0),
                      0
                    )
                    const lastError =
                      d.metadata && typeof d.metadata === 'object' ? d.metadata.last_error : null
                    const canSearch = d.sync_status === 'synced'
                    return (
                      <tr key={d.id}>
                        <td style={td}>{d.title}</td>
                        <td style={td}>
                          <StatusCell status={d.sync_status} lastError={lastError} />
                        </td>
                        <td style={td}>{formatBytes(size)}</td>
                        <td style={td}>
                          <div style={actionsRow}>
                            {canSearch ? (
                              <Link
                                to={`/knowledge?q=${encodeURIComponent(d.title)}&personal=1`}
                                style={{
                                  ...btnSecondary,
                                  textDecoration: 'none',
                                  display: 'inline-block',
                                }}
                              >
                                Search
                              </Link>
                            ) : null}
                            <button
                              type="button"
                              style={btnSecondary}
                              disabled={busy}
                              onClick={() => requestDelete(d.id, d.title)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!busy) {
            setDeleteTarget(null)
            setDeleteError('')
          }
        }}
        title="Remove file and search index?"
        description="This deletes the file from storage and removes it from Knowledge Search. This cannot be undone."
        highlight={deleteTarget?.title}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        busyLabel="Deleting…"
        variant="danger"
        busy={busy}
        error={deleteError}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
